import { useState, useEffect } from "react";
import { loadRecentRounds } from "@/lib/recentRounds";
import { formatMoney, holePayout } from "@/lib/game";
import { Button } from "@/components/ui/button";

export default function HistoryScreen({ onBack, onResume }) {
    const [rounds, setRounds] = useState([]);

    useEffect(() => {
        setRounds(loadRecentRounds());
    }, [setRounds]);

    const formatDate = (ms) => {
        const d = new Date(ms);
        return d.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen flex flex-col" data-testid="history-screen">
            <div
                className="sticky top-0 z-30 border-b hairline"
                style={{ background: "rgba(8,9,10,0.94)", backdropFilter: "blur(8px)" }}
            >
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                    <button
                        onClick={onBack}
                        data-testid="history-back-button"
                        className="text-[color:var(--smoke)] hover:text-[color:var(--bone)] text-xs uppercase tracking-[0.2em]"
                    >
                        ← Back
                    </button>
                    <div className="flex-1 text-center">
                        <div className="font-mono text-lg text-[color:var(--bone)] uppercase tracking-widest">
                            Round History
                        </div>
                    </div>
                    <div className="w-[50px]"></div>
                </div>
            </div>

            <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8 pb-32">
                {rounds.length === 0 ? (
                    <div className="text-center text-[color:var(--smoke)] py-12">
                        No finished rounds yet. Play a round to see it here!
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {rounds.map((r) => {
                            const startingBet = r.course?.startingBet ?? 3;
                            const totalWagered = r.holes.reduce(
                                (a, _, i) => a + holePayout(i, r.holes, startingBet),
                                0,
                            );

                            return (
                                <div key={r.id} className="surface hairline p-5" style={{ borderRadius: 3 }}>
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <div className="font-display text-xl font-[700] mb-1">
                                                {r.course?.courseLabel || "Custom Course"}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--smoke)]">
                                                {formatDate(r.savedAt)} · {r.players.length} Players
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="eyebrow">Pot</div>
                                            <div className="font-mono text-lg text-[color:var(--volt)]">
                                                ${totalWagered}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                                        {r.players.map((p, i) => {
                                            let winnings = 0;
                                            r.holes.forEach((h, hi) => {
                                                const res = h.result;
                                                if (res && res.status === "won") {
                                                    const payout = holePayout(hi, r.holes, startingBet);
                                                    const share = payout / (r.players.length - 1);
                                                    if (res.winnerIdx === i) winnings += payout;
                                                    else winnings -= share;
                                                }
                                            });
                                            return (
                                                <div key={i} className="surface hairline p-2" style={{ borderRadius: 3 }}>
                                                    <div className="text-[10px] uppercase tracking-[0.15em] text-[color:var(--smoke)] truncate">
                                                        {p.name || `P${i + 1}`}
                                                    </div>
                                                    <div className={`font-mono mt-1 ${winnings > 0 ? "text-[color:var(--volt)]" : winnings < 0 ? "text-[color:var(--danger)]" : "text-[color:var(--bone)]"}`}>
                                                        {winnings > 0 ? "+" : ""}{formatMoney(winnings)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex justify-end">
                                        <Button
                                            onClick={() => onResume(r)}
                                            className="btn-ghost"
                                        >
                                            View Scorecard
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
